import { Blueprint } from '@/components/ui/Blueprint';
import { CardKicker } from '@/components/ui/Card';
import { MonoData } from '@/components/ui/MonoData';
import { ptBR } from '@/i18n/pt-BR';
import type { SignatureRecordData } from '@/lib/case_signatures';
import { GenesisOverlay, GENESIS_TYPE_COLOR } from './GenesisOverlay';
import { useSignatureBlob } from './useSignatureBlob';
import {
  useSignatureGraphicGenesis,
  type SignatureGraphicGenesisState,
} from './useSignatureGraphicGenesis';

export interface GenesisPanelProps {
  caseId: string;
  contested: SignatureRecordData;
  selectedPattern: SignatureRecordData | null;
  /** Controlado pelo seletor segmentado (E29.F1): o corpo só monta quando aberto. */
  open: boolean;
}

/**
 * Gênese gráfica — K|Q lado a lado (E26.F2/ADR-0037). // ux-neutrality:allow doc-comment
 *
 * Painel colapsado por padrão (payload buscado on-demand ao expandir, // ux-neutrality:allow doc-comment
 * padrão TopographyPanel). Honestidade por construção: extremidades // ux-neutrality:allow doc-comment
 * marcadas SEM atribuir ataque/remate (imagem estática não registra ordem // ux-neutrality:allow doc-comment
 * de execução); paradas rotuladas "estimadas"; contagens lado a lado sem // ux-neutrality:allow doc-comment
 * veredito — a leitura comparativa é do perito (ADR-0026 preservada). // ux-neutrality:allow doc-comment
 *
 * E54.F6.T07 — mesma família visual do painel de topografia na prancha 2C, e a // ux-neutrality:allow doc-comment
 * paleta de instrumento passa a viver dentro do sistema (ver GenesisOverlay). // ux-neutrality:allow doc-comment
 */
export function GenesisPanel({
  caseId,
  contested,
  selectedPattern,
  open,
}: GenesisPanelProps): JSX.Element | null {
  if (!open) return null;

  return (
    <section className="genesis-panel" aria-label={ptBR.workspaceCaso.genesisTitle}>
      <GenesisMaps caseId={caseId} contested={contested} selectedPattern={selectedPattern} />
    </section>
  );
}

function GenesisMaps({
  caseId,
  contested,
  selectedPattern,
}: Omit<GenesisPanelProps, 'open'>): JSX.Element {
  return (
    <Blueprint className="genesis-body" id="genesis-body" data-testid="genesis-body">
      <div className="flex flex-wrap items-end justify-between gap-sc-4">
        <CardKicker>{ptBR.workspaceCaso.genesisTitle}</CardKicker>
        <GenesisLegend />
      </div>
      {/* O recorte fica no filho, não na moldura. */}
      <div className="mt-sc-4 overflow-x-auto">
        <div className="side-by-side">
          <GenesisFigure
            caseId={caseId}
            signatureId={selectedPattern?.id}
            caption={ptBR.workspaceCaso.genesisPatternCaption}
            testId="genesis-pattern"
          />
          <GenesisFigure
            caseId={caseId}
            signatureId={contested.id}
            caption={ptBR.workspaceCaso.genesisContestedCaption}
            testId="genesis-contested"
          />
        </div>
      </div>
      <p className="genesis-disclaimer">{ptBR.workspaceCaso.genesisDisclaimer}</p>
    </Blueprint>
  );
}

/**
 * Legenda dos três tipos. A FORMA é o portador primário (círculo, losango,
 * quadrado) — a cor é instrumentação, e cor sozinha nunca carrega significado
 * no pacote.
 */
function GenesisLegend(): JSX.Element {
  const itens: Array<[string, string, string]> = [
    [GENESIS_TYPE_COLOR.extremities, '●', ptBR.workspaceCaso.genesisExtremities],
    [GENESIS_TYPE_COLOR.junctions, '◆', ptBR.workspaceCaso.genesisJunctions],
    [GENESIS_TYPE_COLOR.estimated_stops, '■', ptBR.workspaceCaso.genesisStops],
  ];
  return (
    <div className="flex flex-wrap items-center gap-sc-4" data-testid="genesis-legend">
      {itens.map(([cor, forma, rotulo]) => (
        <span key={rotulo} className="inline-flex items-center gap-1.5">
          <span aria-hidden style={{ color: cor }}>
            {forma}
          </span>
          <MonoData variant="label">{rotulo}</MonoData>
        </span>
      ))}
    </div>
  );
}

interface GenesisFigureProps {
  caseId: string;
  signatureId: string | undefined;
  caption: string;
  testId: string;
}

function GenesisFigure({ caseId, signatureId, caption, testId }: GenesisFigureProps): JSX.Element {
  const imageSrc = useSignatureBlob(caseId, signatureId);
  const genesis = useSignatureGraphicGenesis(caseId, signatureId);

  return (
    <Blueprint as="figure" className="genesis-figure" data-testid={testId}>
      <GenesisFigureBody imageSrc={imageSrc} genesis={genesis} caption={caption} />
      <figcaption className="flex flex-wrap items-baseline gap-x-sc-2 border-t border-border px-sc-3 py-sc-2">
        <MonoData variant="label">{caption}</MonoData>
        {genesis.data !== null && <GenesisCounts genesis={genesis} />}
      </figcaption>
    </Blueprint>
  );
}

function GenesisCounts({ genesis }: { genesis: SignatureGraphicGenesisState }): JSX.Element | null {
  if (genesis.data === null) return null;
  const { extremities, junctions, estimated_stops } = genesis.data;
  return (
    <MonoData variant="label" className="genesis-counts" data-testid="genesis-counts">
      {`${ptBR.workspaceCaso.genesisExtremities}: ${extremities.length} · ` +
        `${ptBR.workspaceCaso.genesisJunctions}: ${junctions.length} · ` +
        `${ptBR.workspaceCaso.genesisStops}: ${estimated_stops.length}`}
    </MonoData>
  );
}

interface GenesisFigureBodyProps {
  imageSrc: string | null;
  genesis: SignatureGraphicGenesisState;
  caption: string;
}

function GenesisFigureBody({ imageSrc, genesis, caption }: GenesisFigureBodyProps): JSX.Element {
  // Classe F-E26.F3-02: falha técnica NÃO se apresenta como "indisponível" —
  // indisponibilidade é estado forense do material, não da operação.
  if (genesis.error) {
    return (
      <p className="genesis-error" role="alert" data-testid="genesis-error">
        {ptBR.workspaceCaso.genesisError}
      </p>
    );
  }
  if (genesis.unavailable) {
    return <p className="genesis-unavailable">{ptBR.workspaceCaso.genesisUnavailable}</p>;
  }
  if (genesis.data !== null && imageSrc !== null) {
    return (
      <div className="genesis-marked">
        <img
          src={imageSrc}
          alt={`${ptBR.workspaceCaso.genesisTitle} — ${caption}`}
          className="signature-img"
        />
        <GenesisOverlay genesis={genesis.data} />
      </div>
    );
  }
  if (genesis.loading || (genesis.data !== null && imageSrc === null)) {
    return (
      <div className="signature-img min-h-[12rem] animate-pulse bg-fg/[0.06]" role="status">
        <span className="sr-only">{ptBR.workspaceCaso.genesisLoading}</span>
      </div>
    );
  }
  return <p className="genesis-unavailable">{ptBR.workspaceCaso.genesisUnavailable}</p>;
}
